import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { Network } from '../types/models/Network';
import type { ApiResponseNetworkSingle } from '../types/api/ApiResponseNetworks';

interface UseNetworkReturn {
  network: Network | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useNetwork = (id: number): UseNetworkReturn => {
  const [network, setNetwork] = useState<Network | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchNetwork = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/networks/${id}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseNetworkSingle;
      if (data.status === 'success' && data.data?.network) {
        setNetwork(data.data.network);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load network');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the network');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, id]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchNetwork();
    }
  }, [auth.isAuthenticated, fetchNetwork]);

  return { network, loading, error, refetch: fetchNetwork };
};
